import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { AppLayout } from "@/components/layout/AppLayout";
import { useCreateRole, useRoleList } from "../hooks/useRoles";

export default function CreateRolePage() {
  const navigate = useNavigate();
  const createRole = useCreateRole();
  const { data: roles = [] } = useRoleList();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [copyFromRoleId, setCopyFromRoleId] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Role name is required");
      return;
    }
    try {
      const role = await createRole.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        isActive,
        copyFromRoleId: copyFromRoleId || null,
      });
      navigate(`/admin/roles/${role.id}`);
    } catch (err) {
      setError((err as Error).message ?? "Failed to create role");
    }
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Administration" },
        { label: "Role Management", href: "/admin/roles" },
        { label: "New Role" },
      ]}
    >
      <div className="mx-auto w-full max-w-2xl space-y-6">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/admin/roles">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </Button>
          <ShieldCheck className="h-[18px] w-[18px]" strokeWidth={1.5} />
          <h1 className="text-2xl font-semibold tracking-tight">New Role</h1>
        </div>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Role Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Project Lead"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Short description of the role"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="copyFrom">Copy permissions from existing role</Label>
                <Select
                  id="copyFrom"
                  value={copyFromRoleId}
                  onChange={(e) => setCopyFromRoleId(e.target.value)}
                >
                  <option value="">— Start with no permissions —</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                      {r.isSystem ? " (System)" : ""}
                    </option>
                  ))}
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Optional. Copies the permission set from the selected role.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="active"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-4 w-4 rounded border-border"
                />
                <Label htmlFor="active" className="cursor-pointer">
                  Is Active
                </Label>
              </div>

              {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button asChild variant="outline">
                  <Link to="/admin/roles">Cancel</Link>
                </Button>
                <Button type="submit" disabled={createRole.isPending}>
                  {createRole.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                  )}
                  Create Role
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
